import { useCallback, useEffect, useId, useMemo, useRef, useState } from "react";

import { BOT_DIFFICULTIES, type BotDifficulty } from "../../bot/difficulty";
import { ConfirmDialog } from "../../components/ConfirmDialog/ConfirmDialog";
import type { MatchHistoryPort, MatchHistorySummary, MatchRecordV1 } from "../match/persistence";
import {
  botPerspective,
  createHistoryBundle,
  deriveHistoryStatistics,
  MAX_HISTORY_IMPORT_BYTES,
  parseHistoryBundle,
} from "./historyData";

import "./HistoryScreen.css";

type ModeFilter = "all" | "bot" | "local";
type ResultFilter = "all" | "win" | "loss" | "draw" | "black" | "red" | "abandoned";
type PendingFocus =
  | Readonly<{ kind: "heading" }>
  | Readonly<{ kind: "empty" }>
  | Readonly<{ kind: "record"; id: string }>;
type Confirmation = Readonly<{ kind: "clear" }> | Readonly<{ kind: "delete"; id: string }>;

export type HistoryScreenProps = Readonly<{
  history: MatchHistoryPort;
  onBack: () => void;
  onOpen: (record: MatchRecordV1) => void;
  initialFocusId?: string;
}>;

export function HistoryScreen({ history, onBack, onOpen, initialFocusId }: HistoryScreenProps) {
  const titleId = useId();
  const title = useRef<HTMLHeadingElement>(null);
  const fileInput = useRef<HTMLInputElement>(null);
  const clearButton = useRef<HTMLButtonElement>(null);
  const deleteButton = useRef<HTMLButtonElement>(null);
  const emptyHeading = useRef<HTMLHeadingElement>(null);
  const rowButtons = useRef(new Map<string, HTMLButtonElement>());
  const pendingFocus = useRef<PendingFocus | null>(
    initialFocusId === undefined ? { kind: "heading" } : { kind: "record", id: initialFocusId },
  );
  const [summaries, setSummaries] = useState<readonly MatchHistorySummary[] | null>(null);
  const [failure, setFailure] = useState<string | null>(null);
  const [status, setStatus] = useState<string>("");
  const [mode, setMode] = useState<ModeFilter>("all");
  const [result, setResult] = useState<ResultFilter>("all");
  const [difficulty, setDifficulty] = useState<"all" | BotDifficulty>("all");
  const [confirmation, setConfirmation] = useState<Confirmation | null>(null);

  const reload = useCallback(async (): Promise<void> => {
    const loaded = await history.list();
    if (loaded.kind === "unavailable") {
      setFailure(loaded.reason.message);
      setSummaries(null);
      return;
    }
    setFailure(null);
    setSummaries(loaded.value);
  }, [history]);

  useEffect(() => {
    const initialLoad = globalThis.setTimeout(() => void reload(), 0);
    const unsubscribe = history.subscribe(() => void reload());
    return () => {
      globalThis.clearTimeout(initialLoad);
      unsubscribe();
    };
  }, [history, reload]);

  useEffect(() => {
    const target = pendingFocus.current;
    if (target === null || summaries === null) return;
    pendingFocus.current = null;
    if (target.kind === "heading") title.current?.focus();
    else if (target.kind === "empty") emptyHeading.current?.focus();
    else rowButtons.current.get(target.id)?.focus();
  }, [summaries]);

  const stats = useMemo(() => deriveHistoryStatistics(summaries ?? []), [summaries]);
  const filtered = useMemo(
    () => (summaries ?? []).filter((summary) => matchesFilters(summary, mode, result, difficulty)),
    [difficulty, mode, result, summaries],
  );

  const changeMode = (next: ModeFilter): void => {
    setMode(next);
    setResult("all");
    setDifficulty("all");
  };

  const exportHistory = async (): Promise<void> => {
    const loaded = await history.loadAll();
    if (loaded.kind === "unavailable") {
      setStatus(`Export failed: ${loaded.reason.message}`);
      return;
    }
    const json = `${JSON.stringify(createHistoryBundle(loaded.value), null, 2)}\n`;
    const url = URL.createObjectURL(new Blob([json], { type: "application/json" }));
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = `passo-history-${new Date().toISOString().slice(0, 10)}.json`;
    anchor.click();
    URL.revokeObjectURL(url);
    setStatus(
      `Exported ${loaded.value.length} ${loaded.value.length === 1 ? "match" : "matches"}.`,
    );
  };

  const importFile = async (file: File): Promise<void> => {
    if (file.size > MAX_HISTORY_IMPORT_BYTES) {
      setStatus("Import failed: History file is larger than 20 MiB.");
      return;
    }
    const parsed = await parseHistoryBundle(await file.text());
    if (!parsed.ok) {
      setStatus(`Import failed: ${parsed.message}`);
      return;
    }
    const merged = await history.merge(parsed.records);
    if (merged.kind === "conflict")
      setStatus(`Import failed: Match ${merged.id} conflicts with local history.`);
    else if (merged.kind === "unavailable") setStatus(`Import failed: ${merged.reason.message}`);
    else {
      setStatus(
        `Imported ${merged.added}; skipped ${merged.skipped} duplicate${merged.skipped === 1 ? "" : "s"}.`,
      );
      await reload();
    }
  };

  const confirmMutation = async (): Promise<void> => {
    const target = confirmation;
    setConfirmation(null);
    if (target === null) return;
    let focusAfterCommit: PendingFocus;
    if (target.kind === "clear") focusAfterCommit = { kind: "empty" };
    else {
      const index = filtered.findIndex((item) => item.id === target.id);
      const nextId = filtered[index + 1]?.id ?? filtered[index - 1]?.id;
      focusAfterCommit = nextId ? { kind: "record", id: nextId } : { kind: "empty" };
    }
    const changed =
      target.kind === "clear" ? await history.clear() : await history.delete(target.id);
    if (changed.kind === "unavailable") {
      setStatus(changed.reason.message);
      return;
    }
    pendingFocus.current = focusAfterCommit;
    setStatus(target.kind === "clear" ? "History cleared." : "Match deleted.");
    await reload();
  };

  return (
    <section className="history-screen" aria-labelledby={titleId}>
      <header className="history-screen__header">
        <button
          className="history-icon-button history-screen__back"
          type="button"
          aria-label="Back"
          title="Back"
          onClick={onBack}
        >
          <HistoryIcon name="back" />
        </button>
        <div>
          <p>On this device</p>
          <h1 id={titleId} ref={title} tabIndex={-1}>
            Match history
          </h1>
        </div>
      </header>

      {failure !== null ? (
        <div className="history-screen__empty">
          <h2 tabIndex={-1}>History unavailable</h2>
          <p>{failure}</p>
          <button type="button" onClick={() => void reload()}>
            Try again
          </button>
        </div>
      ) : summaries === null ? (
        <p role="status">Loading history…</p>
      ) : (
        <>
          {summaries.length === 0 ? null : <HistoryStats stats={stats} />}
          <div className="history-screen__tools">
            <button
              className="history-icon-button"
              type="button"
              aria-label="Import history"
              title="Import history"
              onClick={() => fileInput.current?.click()}
            >
              <HistoryIcon name="import" />
            </button>
            <button
              className="history-icon-button"
              type="button"
              aria-label="Export all history"
              title="Export all history"
              disabled={summaries.length === 0}
              onClick={() => void exportHistory()}
            >
              <HistoryIcon name="export" />
            </button>
            <button
              ref={clearButton}
              className="history-icon-button history-screen__danger"
              type="button"
              aria-label="Clear all history"
              title="Clear all history"
              disabled={summaries.length === 0}
              onClick={() => setConfirmation({ kind: "clear" })}
            >
              <HistoryIcon name="trash" />
            </button>
            <input
              ref={fileInput}
              className="history-screen__file"
              type="file"
              aria-label="Import history file"
              accept="application/json,.json"
              onChange={(event) => {
                const file = event.currentTarget.files?.[0];
                event.currentTarget.value = "";
                if (file) void importFile(file);
              }}
            />
          </div>
          <div className="history-screen__filters" aria-label="History filters" role="group">
            <fieldset className="history-screen__mode-filter">
              <legend>Mode</legend>
              <div className="history-screen__segments">
                <button
                  type="button"
                  aria-label="All matches"
                  aria-pressed={mode === "all"}
                  onClick={() => changeMode("all")}
                >
                  <HistoryIcon name="all" />
                  <span>All</span>
                </button>
                <button
                  type="button"
                  aria-label="Bot matches"
                  aria-pressed={mode === "bot"}
                  onClick={() => changeMode("bot")}
                >
                  <HistoryIcon name="bot" />
                  <span>Bot</span>
                </button>
                <button
                  type="button"
                  aria-label="Two-player matches"
                  aria-pressed={mode === "local"}
                  onClick={() => changeMode("local")}
                >
                  <HistoryIcon name="players" />
                  <span>Two players</span>
                </button>
              </div>
            </fieldset>
            <div className="history-screen__adaptive-filters">
              {mode === "bot" ? (
                <label>
                  Result
                  <select
                    value={result}
                    onChange={(event) => setResult(event.currentTarget.value as ResultFilter)}
                  >
                    <option value="all">All</option>
                    <option value="win">Wins</option>
                    <option value="loss">Losses</option>
                    <option value="draw">Draws</option>
                    <option value="abandoned">Abandoned</option>
                  </select>
                </label>
              ) : null}
              {mode === "local" ? (
                <label>
                  Result
                  <select
                    value={result}
                    onChange={(event) => setResult(event.currentTarget.value as ResultFilter)}
                  >
                    <option value="all">All</option>
                    <option value="black">Black wins</option>
                    <option value="red">Red wins</option>
                    <option value="draw">Draws</option>
                    <option value="abandoned">Abandoned</option>
                  </select>
                </label>
              ) : null}
              {mode === "bot" ? (
                <label>
                  Difficulty
                  <select
                    value={difficulty}
                    onChange={(event) =>
                      setDifficulty(event.currentTarget.value as "all" | BotDifficulty)
                    }
                  >
                    <option value="all">All</option>
                    {BOT_DIFFICULTIES.map((item) => (
                      <option key={item} value={item}>
                        {capitalize(item)}
                      </option>
                    ))}
                  </select>
                </label>
              ) : null}
            </div>
          </div>
          {summaries.length === 0 ? (
            <div className="history-screen__empty">
              <h2 ref={emptyHeading} tabIndex={-1}>
                No matches yet
              </h2>
              <p>Completed and abandoned matches will be saved here automatically.</p>
            </div>
          ) : filtered.length === 0 ? (
            <div className="history-screen__empty">
              <h2>No matching matches</h2>
              <p>Change the filters to see more history.</p>
            </div>
          ) : (
            <ol className="history-list">
              {filtered.map((summary) => (
                <HistoryRow
                  key={summary.id}
                  summary={summary}
                  openRef={(button) => {
                    if (button === null) rowButtons.current.delete(summary.id);
                    else rowButtons.current.set(summary.id, button);
                  }}
                  onDelete={(button) => {
                    deleteButton.current = button;
                    setConfirmation({ kind: "delete", id: summary.id });
                  }}
                  onOpen={() => {
                    void history.load(summary.id).then((loaded) => {
                      if (loaded.kind === "loaded" && loaded.value !== null) onOpen(loaded.value);
                      else
                        setStatus(
                          loaded.kind === "unavailable"
                            ? loaded.reason.message
                            : "That match no longer exists.",
                        );
                    });
                  }}
                />
              ))}
            </ol>
          )}
        </>
      )}
      <p className="history-screen__status" role="status">
        {status}
      </p>
      <ConfirmDialog
        open={confirmation !== null}
        title={confirmation?.kind === "clear" ? "Clear all match history?" : "Delete this match?"}
        description={
          confirmation?.kind === "clear"
            ? "Every match stored on this device will be deleted. This cannot be undone."
            : "This match and its replay will be deleted. This cannot be undone."
        }
        confirmLabel={confirmation?.kind === "clear" ? "Clear all" : "Delete"}
        tone="danger"
        invokerRef={confirmation?.kind === "clear" ? clearButton : deleteButton}
        onCancel={() => setConfirmation(null)}
        onConfirm={() => void confirmMutation()}
      />
    </section>
  );
}

function HistoryStats({ stats }: Readonly<{ stats: ReturnType<typeof deriveHistoryStatistics> }>) {
  return (
    <section className="history-stats" aria-labelledby="history-stats-title">
      <details className="history-stats__details">
        <summary>
          <span id="history-stats-title" role="heading" aria-level={2}>
            Your record
          </span>
          <span className="history-stats__summary">
            {stats.botGames === 0
              ? "No bot games"
              : `${stats.record.wins}–${stats.record.losses}–${stats.record.draws} against bots`}
            {` · ${stats.localGames} two-player`}
          </span>
        </summary>
        <div className="history-stats__body">
          <div className="history-stats__grid">
            <Stat label="Bot games" value={String(stats.botGames)} />
            <Stat
              label="W–L–D"
              value={`${stats.record.wins}–${stats.record.losses}–${stats.record.draws}`}
            />
            <Stat
              label="Win rate"
              value={stats.winPercentage === null ? "—" : `${stats.winPercentage}%`}
            />
            <Stat
              label="Current / best streak"
              value={`${stats.currentStreak} / ${stats.longestStreak}`}
            />
            <Stat
              label="Average moves"
              value={stats.averageMoves === null ? "—" : stats.averageMoves.toFixed(1)}
            />
            <Stat label="Two-player games" value={String(stats.localGames)} />
          </div>
          {stats.recent.length > 0 ? (
            <p className="history-stats__recent">
              <strong>Recent:</strong>{" "}
              {stats.recent.map((item) => item.charAt(0).toUpperCase()).join(" · ")}
              <span className="visually-hidden">, wins losses and draws newest first</span>
            </p>
          ) : null}
          <details className="history-stats__breakdown-details">
            <summary>Breakdowns</summary>
            <div className="history-stats__breakdowns">
              <RecordTable
                title="By difficulty"
                rows={BOT_DIFFICULTIES.map((difficulty) => [
                  capitalize(difficulty),
                  stats.byDifficulty[difficulty],
                ])}
              />
              <RecordTable
                title="By color"
                rows={(["black", "red"] as const).map((color) => [
                  capitalize(color),
                  stats.byColor[color],
                ])}
              />
            </div>
          </details>
        </div>
      </details>
    </section>
  );
}
function Stat({ label, value }: Readonly<{ label: string; value: string }>) {
  return (
    <p>
      <span>{label}</span>
      <strong>{value}</strong>
    </p>
  );
}
function RecordTable({
  title,
  rows,
}: Readonly<{
  title: string;
  rows: readonly (readonly [string, { wins: number; losses: number; draws: number }])[];
}>) {
  return (
    <table>
      <caption>{title}</caption>
      <thead>
        <tr>
          <th>Group</th>
          <th>W</th>
          <th>L</th>
          <th>D</th>
        </tr>
      </thead>
      <tbody>
        {rows.map(([label, row]) => (
          <tr key={label}>
            <th>{label}</th>
            <td>{row.wins}</td>
            <td>{row.losses}</td>
            <td>{row.draws}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function HistoryRow({
  summary,
  onOpen,
  onDelete,
  openRef,
}: Readonly<{
  summary: MatchHistorySummary;
  onOpen: () => void;
  onDelete: (button: HTMLButtonElement) => void;
  openRef: (button: HTMLButtonElement | null) => void;
}>) {
  const outcome = outcomeLabel(summary);
  return (
    <li>
      <button ref={openRef} className="history-list__open" type="button" onClick={onOpen}>
        <span
          className={`history-list__mark history-list__mark--${outcome.tone}`}
          aria-hidden="true"
        >
          <HistoryIcon name={outcome.tone === "draw" ? "draw" : "result"} />
        </span>
        <span className="history-list__details">
          <span className={`history-list__outcome history-list__outcome--${outcome.tone}`}>
            {outcome.label}
          </span>
          <span className="history-list__context">
            {summary.mode === "bot"
              ? `${capitalize(summary.difficulty ?? "medium")} · You played ${capitalize(summary.humanPlayer ?? "black")}`
              : "Two players"}
          </span>
          <span className="history-list__meta">
            {new Intl.DateTimeFormat(undefined, { dateStyle: "medium", timeStyle: "short" }).format(
              summary.completedAt,
            )}{" "}
            · {summary.moveCount} {summary.moveCount === 1 ? "move" : "moves"} ·{" "}
            {endingLabel(summary)}
          </span>
        </span>
        <span className="history-list__chevron" aria-hidden="true">
          <HistoryIcon name="next" />
        </span>
      </button>
      <button
        className="history-icon-button history-list__delete"
        type="button"
        title="Delete match"
        aria-label={`Delete ${outcome.label.toLowerCase()} from ${new Date(summary.completedAt).toLocaleDateString()}`}
        onClick={(event) => onDelete(event.currentTarget)}
      >
        <HistoryIcon name="trash" />
      </button>
    </li>
  );
}

type HistoryIconName =
  "all" | "back" | "bot" | "draw" | "export" | "import" | "next" | "players" | "result" | "trash";

function HistoryIcon({ name }: Readonly<{ name: HistoryIconName }>) {
  const path =
    name === "back" ? (
      <path d="m14.5 18-6-6 6-6" />
    ) : name === "import" ? (
      <>
        <path d="M12 3v12" />
        <path d="m7 10 5 5 5-5" />
        <path d="M5 21h14" />
      </>
    ) : name === "export" ? (
      <>
        <path d="M12 21V9" />
        <path d="m7 14 5-5 5 5" />
        <path d="M5 3h14" />
      </>
    ) : name === "trash" ? (
      <>
        <path d="M4 7h16" />
        <path d="m9 7 .5-3h5l.5 3" />
        <path d="m6.5 7 1 14h9l1-14" />
      </>
    ) : name === "bot" ? (
      <>
        <rect x="4" y="7" width="16" height="12" rx="3" />
        <path d="M12 3v4" />
        <circle cx="9" cy="13" r="1" />
        <circle cx="15" cy="13" r="1" />
        <path d="M9 16h6" />
      </>
    ) : name === "players" ? (
      <>
        <circle cx="8" cy="8" r="3" />
        <circle cx="17" cy="9" r="2.5" />
        <path d="M2.5 20c.5-4 2.5-6 5.5-6s5 2 5.5 6" />
        <path d="M14 15c3.5-.5 6 1.2 7 4" />
      </>
    ) : name === "all" ? (
      <>
        <path d="M5 6h14" />
        <path d="M5 12h14" />
        <path d="M5 18h14" />
      </>
    ) : name === "next" ? (
      <path d="m9 18 6-6-6-6" />
    ) : name === "draw" ? (
      <>
        <path d="M5 9h14" />
        <path d="M5 15h14" />
      </>
    ) : (
      <>
        <circle cx="12" cy="12" r="8" />
        <path d="m8.5 12 2.2 2.2 4.8-5" />
      </>
    );
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      {path}
    </svg>
  );
}

function matchesFilters(
  summary: MatchHistorySummary,
  mode: ModeFilter,
  result: ResultFilter,
  difficulty: "all" | BotDifficulty,
): boolean {
  if (mode !== "all" && summary.mode !== mode) return false;
  if (difficulty !== "all" && (summary.mode !== "bot" || summary.difficulty !== difficulty))
    return false;
  if (result === "all") return true;
  if (summary.result.kind === "abandoned") return result === "abandoned";
  if (summary.result.kind === "manual-draw" || summary.result.kind === "repetition-draw") {
    return result === "draw";
  }
  if (summary.mode === "bot") return botPerspective(summary) === result;
  return summary.result.winner === result;
}
function outcomeLabel(summary: MatchHistorySummary): { label: string; tone: string } {
  if (summary.result.kind === "abandoned") return { label: "Abandoned", tone: "abandoned" };
  if (summary.result.kind === "manual-draw" || summary.result.kind === "repetition-draw") {
    return { label: "Draw", tone: "draw" };
  }
  if (summary.mode === "local")
    return { label: `${capitalize(summary.result.winner)} wins`, tone: summary.result.winner };
  const perspective = botPerspective(summary);
  return { label: perspective === "win" ? "You won" : "Bot won", tone: perspective };
}
function endingLabel(summary: MatchHistorySummary): string {
  return summary.result.kind === "abandoned"
    ? "New game started"
    : summary.result.kind === "manual-draw"
      ? "Agreed draw"
      : summary.result.kind === "repetition-draw"
        ? "Threefold repetition"
        : summary.result.kind === "passage"
          ? "Passage"
          : "No legal moves";
}
function capitalize(value: string): string {
  return `${value.charAt(0).toUpperCase()}${value.slice(1)}`;
}
